import React, { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { UnavailableWindow, UnavailableWindowReasons } from '@m4a3/api-client';
import { DateTime } from 'luxon';
import { isoToTimeString } from '../../utils/date-utils';
import { LandingPad } from '../../models/landing-pad.interface';
import { useSelector } from 'react-redux';
import { getActiveCraft } from '../../selectors/liveAircraftSelectors';
import { orderfulApi } from '../../services/api-instances';

interface UnavailableBlockModalProps {
  readonly onClose: () => void;
  readonly cellId: number;
  readonly timezone: string;
  readonly unavailableWindow?: UnavailableWindow;
  readonly timetablePads: LandingPad[];
}

export default function UnavailableBlockModal(props: UnavailableBlockModalProps) {
  const onlineDrones = useSelector(getActiveCraft);

  const isEditing = !!props.unavailableWindow;
  const [startTime, setStartTime] = useState<string>(isEditing ? isoToTimeString(props.unavailableWindow.startDate) : isoToTimeString(new Date().toISOString()));
  const [endTime, setEndTime] = useState<string>(isEditing ? isoToTimeString(props.unavailableWindow.endDate) : isoToTimeString(new Date().toISOString()));
  const [reason, setReason] = useState<string>(isEditing ? props.unavailableWindow.reason : UnavailableWindowReasons.WEATHER_RAIN);
  const [limitToUavId, setLimitToUavId] = useState<number>(isEditing ? props.unavailableWindow.limitToUavId : null);
  const [totalPadList, setTotalPadList] = useState<LandingPad[]>([]);
  const cancelButtonRef = useRef<HTMLButtonElement>  (null);

  useEffect(() => {
    // Filter pads with aircraft assigned
    const padList = props.timetablePads?.filter(pad => pad.aircraft !== 0) || [];
    setTotalPadList(padList);
  }, [onlineDrones, props.timetablePads]);

  const handleStartTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;
    if (time) {
      setStartTime(time);
    } else {
      setStartTime('');
    }
  };

  const handleEndTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const time = event.target.value;
    if (time) {
      setEndTime(time);
    } else {
      setEndTime('');
    }
  };

  const onPressSaveUnavailableWindow = async () => {
    if (isEditing) {
      await updateUnavailableWindow();
    } else {
      await saveUnavailableWindow();
    }
    props.onClose();
  };

  const onPressDeleteUnavailableWindow = async () => {
    await orderfulApi.deleteUnavailableWindowForCell(props.cellId, props.unavailableWindow.id);
    props.onClose()
  };

  const timeStringToISO = (time: string): string => {
    const now = DateTime.local();
    const [hours, minutes] = time.split(':').map(Number);
    const updatedDateTime = now.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

    return updatedDateTime.toJSDate().toISOString();
  };

  const saveUnavailableWindow = async () => {
    return orderfulApi.createUnavailabileWindowForCell(props.cellId, {
      cellId: props.cellId,
      startDate: timeStringToISO(startTime),
      endDate: timeStringToISO(endTime),
      reason,
      limitToUavId: limitToUavId || null
    } as UnavailableWindow)
  };

  const updateUnavailableWindow = async () => {
    return orderfulApi.updateUnavailabileWindowForCell(props.cellId, {
      ...props.unavailableWindow,
      startDate: timeStringToISO(startTime),
      endDate: timeStringToISO(endTime),
      reason,
      limitToUavId: limitToUavId || null
    } as UnavailableWindow)
  };

  // Helper function to determine if the selected reason will close the cell
  const shouldShowCellClosureWarning = (selectedReason: string): boolean => {
    const reasonsThatDontCloseCell = [
      UnavailableWindowReasons.MAINTENANCE,
      UnavailableWindowReasons.STAFF_BREAK,
      UnavailableWindowReasons.OPS_BUFFER,
      UnavailableWindowReasons.OTHER
    ];
    return !reasonsThatDontCloseCell.includes(selectedReason as UnavailableWindowReasons);
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Unavailable block
                      </Dialog.Title>
                      <div className="mt-2">
                        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                          <div className="sm:col-span-3">
                            <label htmlFor="start-time" className="block text-sm font-medium leading-6 text-gray-900">
                              Start time
                            </label>
                            <div className="mt-2">
                              <input
                                id="start-time"
                                type="time"
                                value={isoToTimeString(startTime)}
                                onChange={handleStartTimeChange}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="end-time" className="block text-sm font-medium leading-6 text-gray-900">
                              End time
                            </label>
                            <div className="mt-2">
                              <input
                                type="time"
                                name="end-time"
                                id="end-time"
                                value={isoToTimeString(endTime)}
                                onChange={handleEndTimeChange}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                          </div>
                          <div className="sm:col-span-3">
                            <label htmlFor="reason" className="block text-sm font-medium leading-6 text-gray-900">
                              Reason
                            </label>
                            <div className="mt-2">
                              <select
                                id="reason"
                                name="reason"
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:max-w-xs sm:text-sm sm:leading-6"
                              >
                                <option value={UnavailableWindowReasons.WEATHER_RAIN}>Rain</option>
                                <option value={UnavailableWindowReasons.WEATHER_RAIN_SOON}>Rain coming soon</option>
                                <option value={UnavailableWindowReasons.WEATHER_WIND}>High wind</option>
                                <option value={UnavailableWindowReasons.WEATHER_FOG}>Fog</option>
                                <option value={UnavailableWindowReasons.WEATHER_SNOW}>Snow</option>
                                <option value={UnavailableWindowReasons.WEATHER_STORM}>Storm</option>
                                <option value={UnavailableWindowReasons.MAINTENANCE}>Maintenance</option>
                                <option value={UnavailableWindowReasons.STAFF_BREAK}>Staff break</option>
                                <option value={UnavailableWindowReasons.OPS_BUFFER}>Ops buffer</option>
                                <option value={UnavailableWindowReasons.OTHER}>Other</option>
                              </select>
                            </div>
                            {shouldShowCellClosureWarning(reason) && (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                                                 <div className="flex">
                                   <div className="flex-shrink-0">
                                     <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                       <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                     </svg>
                                   </div>
                                  <div className="ml-3">
                                    <p className="text-sm text-amber-800">
                                      Please note, the selected reason will close the cell even if limited to one pad 
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="sm:col-span-3">
                            <label htmlFor="limitToPad" className="block text-sm font-medium leading-6 text-gray-900">
                              Limit to specific pad
                            </label>
                            <div className="mt-2">
                              <select
                                id="limitToPad"
                                name="limitToPad"
                                value={limitToUavId}
                                onChange={e => {
                                  const aircraftNumber = e.target.value === '' ? 0 : parseInt(e.target.value);
                                  setLimitToUavId(aircraftNumber)
                                }}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:max-w-xs sm:text-sm sm:leading-6"
                              >
                                <option value="">Apply to all pads</option>
                                {totalPadList?.
                                  filter(pad => pad.name.toLowerCase() !== 'unassigned')
                                  .map((pad) => {
                                    return <option key={pad.name} value={pad.aircraft}>{pad.name} (MNA-{pad.aircraft})</option>
                                  })}
                              </select>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="mr-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={() => onPressSaveUnavailableWindow()}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="sm:ml-auto mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => props.onClose()}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                  {isEditing ? <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    onClick={() => onPressDeleteUnavailableWindow()}
                  >
                    Delete
                  </button> : <></>}
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
